import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Accessory } from '@octave/contracts';
import { Loader2, Package, ShoppingCart } from 'lucide-react';
import { useState } from 'react';
import { BillingCenterSelect } from './BillingCenterSelect';
import { QuantitySelector } from './QuantitySelector';
import { formatPrice } from '@/lib/format';

interface AccessoryDialogProps {
  accessory: Accessory | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (accessory: Accessory, quantity: number) => void;
  isLoading?: boolean;
  siteId: string | null;
  onSiteIdChange: (siteId: string) => void;
}

export function AccessoryDialog({
  accessory,
  isOpen,
  onClose,
  onAddToCart,
  isLoading = false,
  siteId,
  onSiteIdChange,
}: AccessoryDialogProps) {
  const [quantity, setQuantity] = useState(1);

  
  const handleAddToCart = () => {
    if (accessory) onAddToCart(accessory, quantity);
  };

  const handleClose = () => {
    setQuantity(1);
    onClose();
  };

  if (!accessory) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="!max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
          <DialogTitle className="text-lg font-bold text-slate-900">
            Ajouter au panier
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image */}
            <div className="aspect-square flex items-center justify-center rounded-2xl bg-slate-50 p-8">
              {accessory.imageUrl ? (
                <img
                  src={accessory.imageUrl}
                  alt={accessory.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Package className="h-24 w-24 text-slate-200" />
              )}
            </div>

            {/* Info */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {accessory.name}
                </h3>
                <p className="text-sm text-slate-400 mt-0.5">
                  {accessory.category}
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-3">
                  {formatPrice(accessory.price)}
                </p>
              </div>

              {/* Cart section */}
              <div className="rounded-xl bg-slate-50 p-5 space-y-4">
                <div className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">
                    Centre de facturation
                  </span>
                  <BillingCenterSelect value={siteId} onChange={onSiteIdChange} />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">
                    Quantité
                  </span>
                  <QuantitySelector
                    value={quantity}
                    onChange={setQuantity}
                    min={1}
                  />
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                  <span className="text-sm font-semibold text-slate-900">
                    Total
                  </span>
                  <span className="text-xl font-bold text-slate-900">
                    {formatPrice(accessory.price * quantity)}
                  </span>
                </div>

                <Button
                  onClick={handleAddToCart}
                  disabled={isLoading || !siteId}
                  className="w-full h-11 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-sm hover:shadow-md transition-all"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Ajout en cours...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-4 w-4" />
                      Ajouter au panier
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
